import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Pencil, Trash2, Receipt, Camera, Loader2, Download,
  Building2, Route as RouteIcon, Package, FileText, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import LearnerHint from "@/components/LearnerHint";
import { todayISO } from "@/lib/format";

const CATEGORIA_META = {
  proyecto: { label: "Proyecto", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Building2 },
  ruta:     { label: "Ruta",     color: "bg-sky-500/15 text-sky-300 border-sky-500/30",         icon: RouteIcon },
  general:  { label: "General",  color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",      icon: Package },
};


const EMPTY = {
  fecha: todayISO(),
  categoria_gasto: "general",
  tipo_gasto: "Otros gastos",
  concepto: "",
  importe: "",
  metodo_pago: "Tarjeta empresa",
  ticket_url: "",
  ticket_public_id: "",
  peticion_id: "",
  ruta_id: "",
  notas: "",
};

export default function Gastos() {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  const [meta, setMeta] = useState({ categorias: [], tipos: [], metodos_pago: [] });
  const [gastos, setGastos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [peticiones, setPeticiones] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [filtros, setFiltros] = useState({
    categoria: "", tipo: "", operario_id: "", desde: "", hasta: "",
  });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---- Cargar metadatos
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/gastos/meta");
        setMeta(data);
      } catch (err) {
        toast.error("Error cargando metadatos");
      }
    })();
  }, []);

  // ---- Cargar peticiones / rutas / usuarios para selects
  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([
          api.get("/peticiones"),
          api.get("/rutas"),
        ]);
        setPeticiones(p.data || []);
        setRutas(r.data || []);
        if (isAdmin) {
          const u = await api.get("/users");
          setUsuarios(u.data || []);
        }
      } catch (err) {
        // silently fail; selects empty
      }
    })();
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.categoria) params.categoria = filtros.categoria;
      if (filtros.tipo) params.tipo = filtros.tipo;
      if (filtros.operario_id && isAdmin) params.operario_id = filtros.operario_id;
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;

      const [g, res] = await Promise.all([
        api.get("/gastos", { params }),
        api.get("/gastos/resumen", { params: { desde: params.desde, hasta: params.hasta } }),
      ]);
      setGastos(g.data || []);
      setResumen(res.data || null);
    } catch (err) {
      toast.error("Error cargando gastos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtros]);

  // ---- Handlers
  const openNew = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (g) => {
    setEdit(g);
    setForm({
      fecha: g.fecha || todayISO(),
      categoria_gasto: g.categoria_gasto || "general",
      tipo_gasto: g.tipo_gasto || "Otros gastos",
      concepto: g.concepto || "",
      importe: g.importe ?? "",
      metodo_pago: g.metodo_pago || "Tarjeta empresa",
      ticket_url: g.ticket_url || "",
      ticket_public_id: g.ticket_public_id || "",
      peticion_id: g.peticion_id || "",
      ruta_id: g.ruta_id || "",
      notas: g.notas || "",
    });
    setOpen(true);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { secure_url, public_id } = await uploadToCloudinary(file, "dnf/gastos");
      setForm((f) => ({ ...f, ticket_url: secure_url, ticket_public_id: public_id }));
      toast.success("Ticket subido");
    } catch (err) {
      toast.error("Error subiendo ticket");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.fecha) return toast.error("La fecha es obligatoria");
    if (form.categoria_gasto === "proyecto" && !form.peticion_id) {
      return toast.error("Selecciona la campaña a imputar");
    }
    if (form.categoria_gasto === "ruta" && !form.ruta_id) {
      return toast.error("Selecciona la ruta a imputar");
    }
    if (!form.importe || parseFloat(form.importe) <= 0) {
      return toast.error("El importe debe ser mayor que 0");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        importe: parseFloat(form.importe),
        peticion_id: form.categoria_gasto === "proyecto" ? form.peticion_id : null,
        ruta_id: form.categoria_gasto === "ruta" ? form.ruta_id : null,
      };
      if (edit) {
        await api.patch(`/gastos/${edit.id}`, payload);
        toast.success("Gasto actualizado");
      } else {
        await api.post("/gastos", payload);
        toast.success("Gasto registrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`¿Eliminar este gasto de ${g.importe} €?`)) return;
    try {
      await api.delete(`/gastos/${g.id}`);
      toast.success("Gasto eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const exportCSV = () => {
    if (!gastos.length) return toast.error("No hay gastos para exportar");
    const header = ["fecha","categoria","tipo","concepto","importe","metodo_pago","operario","vinculo","cliente","notas"];
    const rows = gastos.map((g) => [
      g.fecha, g.categoria_gasto, g.tipo_gasto, g.concepto || "",
      g.importe, g.metodo_pago, g.operario_nombre || g.operario_email || "",
      g.categoria_gasto === "proyecto" ? (g.peticion_codigo || g.peticion_nombre || "")
        : g.categoria_gasto === "ruta" ? (g.ruta_codigo || g.ruta_nombre || "")
        : "",
      g.cliente_nombre || "",
      (g.notas || "").replace(/\n/g, " "),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gastos_${todayISO()}.csv`;
    a.click();
  };

  // ---- KPIs visibles
  const totalFmt = useMemo(
    () => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }),
    []
  );

  return (
    <div className="p-6" data-testid="gastos-page">
      <PageHeader
        title="Gastos"
        subtitle={isAdmin ? "Gastos de equipo, rutas y proyectos" : "Tus gastos imputables"}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportCSV} data-testid="gastos-export-btn"
              className="bg-transparent border-zinc-700 text-zinc-200">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
            <LearnerHint label="Crear un nuevo gasto con foto del ticket">
              <Button onClick={openNew} data-testid="gastos-new-btn"
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400">
                <Plus className="w-4 h-4 mr-2" /> Nuevo gasto
              </Button>
            </LearnerHint>
          </div>
        }
      />

      {/* KPIs */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <KPI label="Total" value={totalFmt.format(resumen.total)} hi data-testid="kpi-total" />
          <KPI label="Proyecto"
               value={totalFmt.format(resumen.por_categoria?.proyecto || 0)}
               accent="text-emerald-300" data-testid="kpi-proyecto" />
          <KPI label="Ruta"
               value={totalFmt.format(resumen.por_categoria?.ruta || 0)}
               accent="text-sky-300" data-testid="kpi-ruta" />
          <KPI label="General"
               value={totalFmt.format(resumen.por_categoria?.general || 0)}
               accent="text-zinc-300" data-testid="kpi-general" />
          <KPI label="Nº gastos"
               value={resumen.count}
               accent="text-amber-300" data-testid="kpi-count" />
        </div>
      )}

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <FilterSelect
          label="Categoría"
          value={filtros.categoria}
          onChange={(v) => setFiltros((f) => ({ ...f, categoria: v }))}
          options={[{ value: "", label: "Todas" }, ...meta.categorias.map((c) => ({ value: c, label: CATEGORIA_META[c]?.label || c }))]}
          testid="filter-categoria"
        />
        <FilterSelect
          label="Tipo"
          value={filtros.tipo}
          onChange={(v) => setFiltros((f) => ({ ...f, tipo: v }))}
          options={[{ value: "", label: "Todos" }, ...meta.tipos.map((t) => ({ value: t, label: t }))]}
          testid="filter-tipo"
        />
        {isAdmin && (
          <FilterSelect
            label="Operario"
            value={filtros.operario_id}
            onChange={(v) => setFiltros((f) => ({ ...f, operario_id: v }))}
            options={[{ value: "", label: "Todos" }, ...usuarios.map((u) => ({ value: u.id, label: u.name }))]}
            testid="filter-operario"
          />
        )}
        <div>
          <Label className="text-xs text-zinc-400">Desde</Label>
          <Input type="date" value={filtros.desde}
            onChange={(e) => setFiltros((f) => ({ ...f, desde: e.target.value }))}
            className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="filter-desde" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Hasta</Label>
          <Input type="date" value={filtros.hasta}
            onChange={(e) => setFiltros((f) => ({ ...f, hasta: e.target.value }))}
            className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="filter-hasta" />
        </div>
        <div className="flex items-end">
          <Button
            variant="ghost" size="sm"
            className="text-zinc-400 hover:text-zinc-100"
            onClick={() => setFiltros({ categoria: "", tipo: "", operario_id: "", desde: "", hasta: "" })}
            data-testid="filter-clear"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Limpiar
          </Button>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
        </div>
      ) : gastos.length === 0 ? (
        <div className="text-center py-20 text-zinc-500 border border-dashed border-zinc-800 rounded-lg" data-testid="gastos-empty">
          <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No hay gastos con los filtros actuales.</p>
          <Button variant="link" onClick={openNew} className="text-amber-400">
            Crear el primero →
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="gastos-list">
          {gastos.map((g) => {
            const meta = CATEGORIA_META[g.categoria_gasto] || CATEGORIA_META.general;
            const Icon = meta.icon;
            const puedeEditar = isAdmin || g.operario_id === user?.id;
            return (
              <div
                key={g.id}
                className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 flex items-center gap-3 hover:border-zinc-700 transition"
                data-testid={`gasto-row-${g.id}`}
              >
                {g.ticket_url ? (
                  <a href={g.ticket_url} target="_blank" rel="noreferrer" className="shrink-0">
                    <img src={cloudinaryThumb(g.ticket_url, 60, 60)} alt="ticket"
                      className="w-12 h-12 rounded object-cover border border-zinc-700" />
                  </a>
                ) : (
                  <div className="w-12 h-12 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-600 shrink-0">
                    <Receipt className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${meta.color}`}>
                      <Icon className="w-3 h-3 inline mr-1" /> {meta.label}
                    </span>
                    <span className="text-xs text-zinc-500">{g.fecha}</span>
                    <span className="text-xs text-zinc-400">· {g.tipo_gasto}</span>
                    {g.categoria_gasto === "proyecto" && (g.peticion_codigo || g.peticion_nombre) && (
                      <span className="text-xs text-emerald-300/80">
                        → {g.peticion_codigo || ""} {g.peticion_nombre || ""}
                        {g.cliente_nombre ? ` · ${g.cliente_nombre}` : ""}
                      </span>
                    )}
                    {g.categoria_gasto === "ruta" && (g.ruta_codigo || g.ruta_nombre) && (
                      <span className="text-xs text-sky-300/80">
                        → {g.ruta_codigo || ""} {g.ruta_nombre || ""}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-100 mt-0.5 truncate">
                    {g.concepto || <span className="italic text-zinc-500">— sin concepto —</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {g.operario_nombre || g.operario_email || "?"} · {g.metodo_pago}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-bold text-zinc-50">{totalFmt.format(g.importe || 0)}</div>
                  {puedeEditar && (
                    <div className="flex justify-end gap-1 mt-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(g)}
                        className="h-7 w-7 text-zinc-400 hover:text-amber-300"
                        data-testid={`gasto-edit-${g.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(g)}
                        className="h-7 w-7 text-zinc-400 hover:text-red-400"
                        data-testid={`gasto-delete-${g.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Crear/Editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="gasto-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar gasto" : "Nuevo gasto"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Categoría · tipo · importe · ticket. Los gastos de ruta se reparten entre clientes.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Fecha *</Label>
                <Input type="date" value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-fecha" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Importe (€) *</Label>
                <Input type="number" step="0.01" min="0" value={form.importe}
                  onChange={(e) => setForm({ ...form, importe: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-importe" required />
              </div>
            </div>

            {/* Categoría */}
            <div>
              <Label className="text-xs text-zinc-400 mb-2 block">Categoría *</Label>
              <div className="grid grid-cols-3 gap-2">
                {meta.categorias.map((c) => {
                  const m = CATEGORIA_META[c] || CATEGORIA_META.general;
                  const Icon = m.icon;
                  const active = form.categoria_gasto === c;
                  return (
                    <button
                      key={c} type="button"
                      onClick={() => setForm({ ...form, categoria_gasto: c, peticion_id: "", ruta_id: "" })}
                      data-testid={`form-cat-${c}`}
                      className={`p-3 rounded-lg border transition text-sm font-medium flex flex-col items-center gap-1 ${
                        active ? m.color : "bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {form.categoria_gasto === "proyecto" && (
              <div>
                <Label className="text-xs text-zinc-400">Campaña / Petición *</Label>
                <Select value={form.peticion_id} onValueChange={(v) => setForm({ ...form, peticion_id: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-peticion">
                    <SelectValue placeholder="Selecciona campaña…" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                    {peticiones.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.codigo ? `${p.codigo} · ` : ""}{p.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.categoria_gasto === "ruta" && (
              <div>
                <Label className="text-xs text-zinc-400">Ruta *</Label>
                <Select value={form.ruta_id} onValueChange={(v) => setForm({ ...form, ruta_id: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-ruta">
                    <SelectValue placeholder="Selecciona ruta…" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                    {rutas.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.codigo ? `${r.codigo} · ` : ""}{r.nombre} ({r.fecha})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Tipo de gasto</Label>
                <Select value={form.tipo_gasto} onValueChange={(v) => setForm({ ...form, tipo_gasto: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {meta.tipos.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Método de pago</Label>
                <Select value={form.metodo_pago} onValueChange={(v) => setForm({ ...form, metodo_pago: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-metodo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {meta.metodos_pago.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Concepto / descripción</Label>
              <Input
                value={form.concepto}
                onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                placeholder="Ej. Gasolina vuelta Sevilla → Madrid"
                className="bg-zinc-900 border-zinc-800"
                data-testid="form-concepto"
              />
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Ticket / Factura (foto)</Label>
              {form.ticket_url ? (
                <div className="relative inline-block">
                  <a href={form.ticket_url} target="_blank" rel="noreferrer">
                    <img src={cloudinaryThumb(form.ticket_url, 200, 200)} alt="ticket"
                      className="w-32 h-32 rounded object-cover border border-zinc-700" />
                  </a>
                  <button type="button" onClick={() => setForm({ ...form, ticket_url: "", ticket_public_id: "" })}
                    className="absolute -top-1 -right-1 bg-red-600 rounded-full p-1 text-white text-xs"
                    data-testid="form-ticket-clear">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer flex items-center gap-2 w-full p-3 rounded border border-dashed border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-900/70 hover:text-zinc-200 transition"
                  data-testid="form-ticket-upload">
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo…</>
                  ) : (
                    <><Camera className="w-4 h-4" /> Subir foto del ticket / factura</>
                  )}
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])} />
                </label>
              )}
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Notas internas (opcional)</Label>
              <Input value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                className="bg-zinc-900 border-zinc-800" data-testid="form-notas" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}
                className="bg-transparent border-zinc-700 text-zinc-300">
                Cancelar
              </Button>
              <Button type="submit" disabled={saving || uploading}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="form-save">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : (edit ? "Guardar cambios" : "Registrar gasto")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Sub-componentes locales ----
function KPI({ label, value, accent = "text-zinc-100", hi = false, "data-testid": testid }) {
  return (
    <div
      className={`rounded-lg border ${hi ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/40"} px-4 py-3`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-lg font-bold ${hi ? "text-amber-300" : accent}`}>{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs text-zinc-400">{label}</Label>
      <Select value={value || "__ALL__"} onValueChange={(v) => onChange(v === "__ALL__" ? "" : v)}>
        <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
          {options.map((o) => (
            <SelectItem key={o.value || "__ALL__"} value={o.value || "__ALL__"}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
